import React from "react";
import { X, ShoppingCart, Tag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PartImageSlider from "../../hooks/partImageSlider";

const PartPaymentModal = ({ part, open, close }) => {
  const handlePayClick = () => {
    if (part?.stripePaymentLink) {
      window.open(part?.stripePaymentLink, "_blank");
    }
  };

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800"
    }
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const tier = getCurrentTier(part);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg w-full flex flex-col p-0 overflow-hidden rounded-2xl shadow-xl">
        <DialogHeader className="flex justify-between items-center px-6 py-4 border-b bg-muted/40">
          <DialogTitle className="text-2xl text-primary font-semibold">
            Checkout
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Secure payment powered by Stripe
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex flex-col gap-6 px-6 py-6 bg-background">
          {/* Part Summary Card */}
          <Card className="flex items-center gap-4 p-4 shadow-sm border rounded-xl">
            <div
              className={`relative w-20 h-20 bg-secondary rounded-md overflow-hidden`}
            >
              <PartImageSlider
                images={
                  part?.imageUrls?.length
                    ? part.imageUrls
                    : part?.imageReferences?.length
                    ? part.imageReferences
                    : part?.imageUrl
                    ? [part.imageUrl]
                    : []
                }
                alt={`Image of ${
                  part?.name == "other"
                    ? part?.sub_name
                    : part?.name || "Unknown Part"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              {tier && (
                <span
                  className={`absolute top-1.5 right-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tier.bgColor} ${tier.textColor} ${tier.borderColor}`}
                >
                  <tier.icon className="mr-1 h-2.5 w-2.5" />
                  {tier.label}
                </span>
              )}
            </div>

            <div className="flex flex-col flex-1">
              <h3 className="font-semibold text-base text-foreground line-clamp-1">
                {part?.name == "other"
                  ? part?.sub_name
                  : part?.name || "Unknown Part"}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-1">
                {part?.year || "N/A"} •{" "}
                {part?.make == "other" ? part?.sub_make : part?.make || ""}{" "}
                {part?.model == "other" ? part?.sub_model : part?.model || ""}
              </p>
              <p className="font-semibold text-primary mt-1">
                ${part?.price ? Number(part.price).toFixed(2) : "N/A"}
              </p>
            </div>
          </Card>

          {/* Payment Info */}
          {part?.stripePaymentLink ? (
            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground mb-4 text-center">
                Click below to complete your purchase securely through Stripe.
              </p>
              <Button
                size="lg"
                className="w-full bg-primary text-white hover:bg-primary/80 flex items-center justify-center gap-2"
                onClick={handlePayClick}
              >
                <ShoppingCart className="h-5 w-5" />
                Pay Now
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-red-500 font-medium text-lg">
                Payment link not available
              </p>
              <p className="text-muted-foreground text-sm mt-2">
                Please contact the seller for assistance.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PartPaymentModal;
